import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Check, X, Star, Zap, Crown,
  DollarSign, CreditCard, List, Save, AlertTriangle
} from 'lucide-react';
import api from '../../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const PLAN_ICONS = {
  inicial: Star,
  basic: Zap,
  socio: Crown
};

const PLAN_COLORS = {
  inicial: 'blue',
  basic: 'green',
  socio: 'purple'
};

export const PlansManager = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPlan, setNewPlan] = useState({
    plan_id: '',
    name: '',
    subtitle: '',
    price: '',
    price_type: 'mes',
    credits: 0,
    features: [''],
    is_recommended: false,
    is_active: true,
    order: 0
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/plans');
      setPlans(response.data);
    } catch (error) {
      // Si no hay planes, inicializarlos
      if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response.data.length === 0)) {
        await initializePlans();
      } else {
        toast.error('Error al cargar planes');
      }
    } finally {
      setLoading(false);
    }
  };

  const initializePlans = async () => {
    try {
      await api.post('/admin/plans/initialize');
      toast.success('Planes inicializados');
      fetchPlans();
    } catch (error) {
      console.error('Error inicializando planes:', error);
    }
  };

  const handleCreatePlan = async () => {
    try {
      const planData = {
        ...newPlan,
        price: newPlan.price === '' ? null : parseFloat(newPlan.price),
        features: newPlan.features.filter(f => f.trim() !== '')
      };
      
      await api.post('/admin/plans', planData);
      toast.success('Plan creado exitosamente');
      setShowCreateModal(false);
      setNewPlan({
        plan_id: '',
        name: '',
        subtitle: '',
        price: '',
        price_type: 'mes',
        credits: 0,
        features: [''],
        is_recommended: false,
        is_active: true,
        order: 0
      });
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear plan');
    }
  };

  const handleUpdatePlan = async (planId) => {
    try {
      const planData = {
        ...editingPlan,
        price: editingPlan.price === '' || editingPlan.price === null ? null : parseFloat(editingPlan.price),
        features: editingPlan.features.filter(f => f.trim() !== '')
      };
      
      await api.put(`/admin/plans/${planId}`, planData);
      toast.success('Plan actualizado');
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar plan');
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!confirm('¿Estás seguro de eliminar este plan?')) return;
    
    try {
      await api.delete(`/admin/plans/${planId}`);
      toast.success('Plan eliminado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar plan');
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await api.put(`/admin/plans/${plan.plan_id}`, { is_active: !plan.is_active });
      toast.success(plan.is_active ? 'Plan desactivado' : 'Plan activado');
      fetchPlans();
    } catch (error) {
      toast.error('Error al cambiar estado del plan');
    }
  };

  const handleSetRecommended = async (planId) => {
    try {
      await api.put(`/admin/plans/${planId}`, { is_recommended: true });
      toast.success('Plan marcado como recomendado');
      fetchPlans();
    } catch (error) {
      toast.error('Error al marcar como recomendado');
    }
  };

  const addFeature = (setter, features) => {
    setter([...features, '']);
  };

  const updateFeature = (setter, features, index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setter(newFeatures);
  };

  const removeFeature = (setter, features, index) => {
    setter(features.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold">Configuración de Planes</h3>
          <p className="text-sm text-muted-foreground">Administrá los planes de membresía disponibles</p>
        </div>
        <div className="flex gap-2">
          {plans.length === 0 && (
            <Button variant="outline" onClick={initializePlans}>
              Inicializar Planes por Defecto
            </Button>
          )}
          <Button onClick={() => setShowCreateModal(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Nuevo Plan
          </Button>
        </div>
      </div>

      {/* Lista de planes */}
      <div className="grid gap-4">
        {plans.map((plan) => {
          const Icon = PLAN_ICONS[plan.plan_id] || CreditCard;
          const color = PLAN_COLORS[plan.plan_id] || 'gray';
          const isEditing = editingPlan?.plan_id === plan.plan_id;
          
          return (
            <Card 
              key={plan.id} 
              className={`glass-effect border-white/10 ${!plan.is_active ? 'opacity-50' : ''} ${
                plan.is_recommended ? 'ring-2 ring-purple-500' : ''
              }`}
            >
              <CardContent className="p-6">
                {isEditing ? (
                  /* Modo edición */
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Nombre</label>
                        <Input
                          value={editingPlan.name}
                          onChange={(e) => setEditingPlan({ ...editingPlan, name: e.target.value })}
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Subtítulo</label>
                        <Input
                          value={editingPlan.subtitle}
                          onChange={(e) => setEditingPlan({ ...editingPlan, subtitle: e.target.value })}
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-1 block">Precio (vacío = consultar)</label>
                        <Input
                          type="number"
                          value={editingPlan.price || ''}
                          onChange={(e) => setEditingPlan({ ...editingPlan, price: e.target.value })}
                          placeholder="Ej: 100000"
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Tipo de precio</label>
                        <select
                          value={editingPlan.price_type}
                          onChange={(e) => setEditingPlan({ ...editingPlan, price_type: e.target.value })}
                          className="w-full p-2 bg-white/5 border border-white/10 rounded"
                        >
                          <option value="mes">Por mes</option>
                          <option value="hora">Por hora</option>
                          <option value="clase">Por clase</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium mb-1 block">Créditos</label>
                        <Input
                          type="number"
                          value={editingPlan.credits}
                          onChange={(e) => setEditingPlan({ ...editingPlan, credits: parseInt(e.target.value) || 0 })}
                          className="bg-white/5 border-white/10"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium mb-2 block">Características</label>
                      <div className="space-y-2">
                        {editingPlan.features.map((feature, idx) => (
                          <div key={idx} className="flex gap-2">
                            <Input
                              value={feature}
                              onChange={(e) => {
                                const newFeatures = [...editingPlan.features];
                                newFeatures[idx] = e.target.value;
                                setEditingPlan({ ...editingPlan, features: newFeatures });
                              }}
                              placeholder="Característica..."
                              className="bg-white/5 border-white/10"
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingPlan({
                                  ...editingPlan,
                                  features: editingPlan.features.filter((_, i) => i !== idx)
                                });
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setEditingPlan({
                            ...editingPlan,
                            features: [...editingPlan.features, '']
                          })}
                        >
                          <Plus className="h-4 w-4 mr-1" /> Agregar característica
                        </Button>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setEditingPlan(null)}>
                        Cancelar
                      </Button>
                      <Button onClick={() => handleUpdatePlan(plan.plan_id)}>
                        <Save className="h-4 w-4 mr-2" /> Guardar
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* Modo visualización */
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-full ${
                      color === 'blue' ? 'bg-blue-500/20' :
                      color === 'green' ? 'bg-green-500/20' :
                      'bg-purple-500/20'
                    }`}>
                      <Icon className={`h-6 w-6 ${
                        color === 'blue' ? 'text-blue-400' :
                        color === 'green' ? 'text-green-400' :
                        'text-purple-400'
                      }`} />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-bold text-lg">{plan.name}</h4>
                        {plan.is_recommended && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full">
                            RECOMENDADO
                          </span>
                        )}
                        {!plan.is_active && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            INACTIVO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{plan.subtitle}</p>
                      
                      <div className="flex items-center gap-4 mb-3">
                        <div className="flex items-center gap-1">
                          <DollarSign className="h-4 w-4 text-muted-foreground" />
                          <span className="font-bold">
                            {plan.price ? `$${plan.price.toLocaleString('es-AR')}` : 'Consultar'}
                          </span>
                          <span className="text-sm text-muted-foreground">/{plan.price_type}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <span>{plan.credits} créditos</span>
                        </div>
                      </div>
                      
                      <ul className="space-y-1">
                        {plan.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                            <Check className="h-3 w-3 text-primary" />
                            {feature}
                          </li>
                        ))}
                        {plan.features.length > 3 && (
                          <li className="text-sm text-muted-foreground">
                            +{plan.features.length - 3} más...
                          </li>
                        )}
                      </ul>
                    </div>
                    
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingPlan({ ...plan })}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleActive(plan)}
                        title={plan.is_active ? 'Desactivar' : 'Activar'}
                      >
                        {plan.is_active ? <X className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                      </Button>
                      {!plan.is_recommended && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetRecommended(plan.plan_id)}
                          title="Marcar como recomendado"
                        >
                          <Star className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeletePlan(plan.plan_id)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
        
        {plans.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hay planes configurados</p>
            <Button onClick={initializePlans} className="mt-4">
              Inicializar Planes por Defecto
            </Button>
          </div>
        )}
      </div>

      {/* Modal crear plan */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-background border border-white/10 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-6">Crear Nuevo Plan</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">ID del Plan</label>
                  <Input
                    value={newPlan.plan_id}
                    onChange={(e) => setNewPlan({ ...newPlan, plan_id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                    placeholder="ej: premium"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Orden</label>
                  <Input
                    type="number"
                    value={newPlan.order}
                    onChange={(e) => setNewPlan({ ...newPlan, order: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Nombre</label>
                <Input
                  value={newPlan.name}
                  onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                  placeholder="Plan Premium"
                  className="bg-white/5 border-white/10"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-1 block">Subtítulo</label>
                <Input
                  value={newPlan.subtitle}
                  onChange={(e) => setNewPlan({ ...newPlan, subtitle: e.target.value })}
                  placeholder="Para jugadores avanzados"
                  className="bg-white/5 border-white/10"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Precio</label>
                  <Input
                    type="number"
                    value={newPlan.price}
                    onChange={(e) => setNewPlan({ ...newPlan, price: e.target.value })}
                    placeholder="Vacío = Consultar"
                    className="bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Tipo</label>
                  <select
                    value={newPlan.price_type}
                    onChange={(e) => setNewPlan({ ...newPlan, price_type: e.target.value })}
                    className="w-full p-2 bg-white/5 border border-white/10 rounded h-10"
                  >
                    <option value="mes">Por mes</option>
                    <option value="hora">Por hora</option>
                    <option value="clase">Por clase</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Créditos</label>
                  <Input
                    type="number"
                    value={newPlan.credits}
                    onChange={(e) => setNewPlan({ ...newPlan, credits: parseInt(e.target.value) || 0 })}
                    className="bg-white/5 border-white/10"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">Características</label>
                <div className="space-y-2">
                  {newPlan.features.map((feature, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => {
                          const newFeatures = [...newPlan.features];
                          newFeatures[idx] = e.target.value;
                          setNewPlan({ ...newPlan, features: newFeatures });
                        }}
                        placeholder="Característica..."
                        className="bg-white/5 border-white/10"
                      />
                      {newPlan.features.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setNewPlan({
                            ...newPlan,
                            features: newPlan.features.filter((_, i) => i !== idx)
                          })}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNewPlan({
                      ...newPlan,
                      features: [...newPlan.features, '']
                    })}
                  >
                    <Plus className="h-4 w-4 mr-1" /> Agregar
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPlan.is_recommended}
                    onChange={(e) => setNewPlan({ ...newPlan, is_recommended: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Marcar como recomendado</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newPlan.is_active}
                    onChange={(e) => setNewPlan({ ...newPlan, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Activo</span>
                </label>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleCreatePlan} className="flex-1">
                Crear Plan
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
